import { defineStore } from 'pinia';
import { ref } from 'vue';

import axios from 'axios';
import { sortBy, each, get, isArray } from 'lodash';
import to from 'await-to-js';
import AssortmentGroupTypes from '@enums/assortment-group-types';

// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useContextStore } from '@/stores/context';
import { useWorkpackagesStore } from '@/stores/workpackages';

import { useDayjs } from '@/composables/use-dayjs';

export const useAssortmentGroupsStore = defineStore('assortmentGroups', () => {
  // states
  const masterAssortmentGroups = ref([]);
  const categoryToAssortmentGroupKeysMap = ref({});
  const loading = ref(false);

  // Private helpers
  function setAssortmentGroupData({ assortmentGroups, options }) {
    // Add the text and sortKey fields
    assortmentGroups = assortmentGroups.map(ag => {
      const productCount = ag.distinctProductCount;
      const storeCount = ag.distinctStoreCount;
      let text;
      if (ag.type === AssortmentGroupTypes.furniture) {
        text = `${ag[options.furnitureCategoryLevelDisplay]}`;
        if (!options.shortAssortmentGroupTextEnabled)
          text += ` (${ag.furnitureCategory1Description})`;
        text += ` (${productCount}-${storeCount}-${ag.distinctFurnitureArchetypeCount})`;
      } else if (ag.type === AssortmentGroupTypes.nonFurniture) {
        text = `${ag.productCategoryDescription} (${ag.formatDescription})`;
        text = `${text} (${productCount}-${storeCount})`;
      }

      return {
        ...ag,
        text,
        sortKey: `${ag.furnitureCategory2Description} ${ag.furnitureCategory1Description}`,
      };
    });
    // Sort and set
    assortmentGroups = sortBy(assortmentGroups, 'sortKey');
    masterAssortmentGroups.value = assortmentGroups;

    // Set the category to assortment group keys lookup
    const agsGroupedByCategory = {};
    each(assortmentGroups, assortmentGroup => {
      const category = assortmentGroup[options.filterType];

      each(isArray(category) ? category : [category], c => {
        if (!agsGroupedByCategory[c]) {
          agsGroupedByCategory[c] = [];
        }

        agsGroupedByCategory[c].push(assortmentGroup.key);
      });
    });
    categoryToAssortmentGroupKeysMap.value = agsGroupedByCategory;
  }

  // Actions
  async function fetchAssortmentGroups(snapshotDate) {
    const currentSnapshotDate = useWorkpackagesStore().wpDateSelections.snapshotDate;
    snapshotDate = snapshotDate || currentSnapshotDate;

    const { dayjs } = useDayjs();
    const contextStore = useContextStore();
    loading.value = true;
    const params = {
      snapshotDate: dayjs.utc(snapshotDate).format('YYYY-MM-DD'),
      pick: [
        // Common fields
        'key',
        'type',
        'distinctStoreCount',
        'distinctProductCount',
        'distinctProductKeys',
        'staticAssortmentGroupKey',
        // Furniture-type fields
        'furnitureCategory1Description',
        'furnitureCategory2Description',
        'distinctFurnitureArchetypeCount',
        'associatedProductCategoryDescriptions',
        // Non-furniture-type fields
        'formatDescription',
        'productCategoryDescription',
        'hasSyntheticFurniture',
      ],
    };

    const [err, response] = await to(axios.get('/api/assortment-groups/', { params }));
    if (err) {
      loading.value = false;
      throw new Error(err.message);
    }
    const payload = {
      assortmentGroups: response.data,
      options: {
        shortAssortmentGroupTextEnabled: get(
          contextStore.clientConfig,
          'features.shortAssortmentGroupTextEnabled',
          false
        ),
        furnitureCategoryLevelDisplay: get(
          contextStore.clientConfig,
          'assortmentGroups.furnitureCategoryLevelDisplay',
          'furnitureCategory2Description'
        ),
        filterType: get(
          contextStore.clientConfig,
          'assortmentGroups.filterType',
          'associatedProductCategoryDescriptions'
        ),
      },
    };
    setAssortmentGroupData(payload);
    loading.value = false;
    return payload;
  }

  return {
    // states
    masterAssortmentGroups,
    categoryToAssortmentGroupKeysMap,
    loading,

    // Actions
    fetchAssortmentGroups,
  };
});
